import Framework, { shapes } from '@greenville/framework';
import { Box, Button, withStyles } from '@material-ui/core';
import { ThemeProvider, createTheme } from '@material-ui/core/styles';
import { Stack } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import _ from 'lodash';
import { toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import TableComponent from '../../../../../common/components/Table';
import ColumnConfig from '../../../../../common/config/ColumnConfig';
import * as constants from '../../../../../common/constants';
import utils from '../../../../../common/utils';
import CustomPagination from '../../../common/components/CustomPagination';
import ExportToCSV from '../../../common/components/ExportToCSV';
import ChatgptUtilityIESUser from '../../../models/ChatgptIESUser';
import ChatgptUtilityMapping from '../../../models/ChatgptMapping';
import InternalUsersList from '../../../models/InternalUsersList';
import ResponseModel from '../../../models/Response';
import TopicQuizDialogComponent from '../../datagrid/TopicQuizDialogComponent';
import DynamicDialog from './DynamicDialog';
import DynamicListing from './DynamicListing';
import ChatgptConfig from '../../../../../common/config/ChatgptConfig';

const themes = createTheme({
  palette: {
    primary: {
      light: '#047a9c',
      main: '#005d83',
      dark: '#003558',
      contrastText: '#ffffff'
    }
  },
  overrides: {
    MuiButton: {
      root: {
        borderRadius: 30
      }
    }
  }
});

const styles = () => ({
  buttonStyle: {
    cursor: 'pointer',
    width: '170px',
    margin: '10px',
    fontSize: '14px',
    background: '#005d83 !important'
  }
});

const DynamicDataGrid = React.memo((props) => {
  const {
    gridData,
    handleAscending,
    classes,
    isNextPageKey,
    handleSearch,
    selectedTab,
    toggleBtnValue,
    ChatgptUtilityIESUserData,
    chatgptResponseUtility,
    chatgptMappingUtility,
    chatgptUtilityResponseStatus,
    tenantName,
    tenantTitle,
    tenantId,
    viewType,
    startDate,
    endDate,
    tenantConfig,
    internalUsers,
    isUserIdExcluded,
    isNextPageKeyEnable,
    paginationData,
    showIngestion,
    searchFields
  } = props;
  const isFeedback = selectedTab === ChatgptConfig.TabList.FEEDBACK;
  const [filteredArray, setFilteredArray] = useState([]);
  const [userEmailDetails, setUserEmailDetails] = useState({});
  const [userTocDetails, setUserTocDetails] = useState({});
  const [openChat, openChatDialog] = useState(false);
  const [loaderStatusResponse] = useState(true);
  const [dataValue, setDataValue] = useState({});
  const [dataLength, setDatalength] = useState({});
  const [directionValue, setDirectionValue] = useState('');
  const [rowDialogue, setRowDialogue] = useState({});
  const [modalIndex, setModalIndex] = useState(0);
  const [selectedValue, setSelectedValue] = useState('');
  const [order, SetOrder] = useState('ASC');
  const [isTopicQuiz, setIsTopicQuiz] = useState(false);
  const [configData, setConfigdata] = useState({ hideUserColumn: false, whetherFetchUserData: false });
  const internalUserIds = internalUsers ? JSON.parse(JSON.stringify(toJS(internalUsers))) : [];
  // Pagination
  const [serverPage, setServerPage] = useState(1);
  const rowsPerPage = 100;
  const totalPages = Math.ceil(paginationData?.total / rowsPerPage);

  const setTenantSpecificConfig = () => {
    const { data } = toJS(tenantConfig);
    if (data.length > 0) {
      const match = data.find((config) => config.name === tenantName);
      if (match) {
        setConfigdata({ hideUserColumn: match.hideUserId, whetherFetchUserData: match.whetherCallIESUser });
      }
    }
  };

  const handleEvent = (params, index) => {
    if (
      [constants.PVS, constants.RPLUS, constants.DIGITALHUB, constants.REVEL].includes(tenantName) &&
      selectedTab === ChatgptConfig.TabList.CONTENT_MGMT
    ) {
      const { courseId } = params;
      const bookId = courseId;
      Framework.redirectTo(`/chatgptutility/${tenantName}/${viewType}/view/${bookId}`);
    } else {
      const { requestId, bookId, type } = params;
      let tenatIdVal = tenantId;
      setRowDialogue(params);
      openChatDialog(true);
      setModalIndex(index);
      setSelectedValue(index);
      if (_.isEmpty(tenantId)) {
        const selectedTabName = window.location.pathname.split('/')[2];
        tenatIdVal = utils.getTenantId(selectedTabName, tenantConfig);
      }
      if (isFeedback) {
        chatgptResponseUtility.fetch({ requestId, bookId, tenantId: tenatIdVal });
      } else {
        const defaultParams = { requestId, bookId, type, tenantId: tenatIdVal };
        chatgptResponseUtility.fetch(defaultParams);
      }
    }
  };

  useEffect(() => {
    // Function to check URL parameters
    const checkUrlParams = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const bookId = urlParams.get('bookId');
      const requestId = urlParams.get('requestId');
      const type = urlParams.get('type');

      if (bookId && requestId && type) {
        setDatalength(1);
        handleEvent({ bookId, requestId, type }, 0);
      }
    };
    if (!isFeedback) {
      checkUrlParams();
    }
  }, []);

  useEffect(() => {
    setServerPage(paginationData?.page);
  }, [paginationData?.page]);

  useEffect(() => {
    setTenantSpecificConfig();
    const data = Array.isArray(gridData) ? gridData : [];
    if (data.length > 0 && isUserIdExcluded && internalUserIds?.userId?.length > 0) {
      const filteredData = data?.filter((item) => !internalUserIds?.userId.includes(item.userId));
      setFilteredArray(filteredData);
      setDatalength(filteredData.length);
    } else {
      setFilteredArray(data);
      setDatalength(gridData.length);
    }

    /* eslint-disable camelcase */
    Framework.getEventManager().on(constants.SET_CHATGPT_IES_USER_DATA, () => {
      const { userData } = ChatgptUtilityIESUserData;
      if (userData.length > 0) {
        setUserEmailDetails(userData[userData.length - 1]);
      }
    });
    Framework.getEventManager().on(constants.SET_CHATGPT_MAPPING_DATA, () => {
      const { toc } = chatgptMappingUtility;
      if (toc.length > 0) {
        setUserTocDetails(toc);
      }
    });
    let rowDialogProcessed = {};
    let rowProcessed = {};
    // eslint-disable-next-line consistent-return
    Framework.getEventManager().on(constants.GET_CHATGPT_RESPONSE_MODEL_DATA, () => {
      const {
        summary,
        quizMcq,
        discuss,
        quizIntent,
        quiz,
        mcq,
        commonAuditData,
        mcqQuestionGeneration,
        summaryTopic,
        summary_enrichment,
        chat_intent,
        explainStandalone,
        flashcards,
        quizFreeResponse,
        quizValidateIntent,
        salutation,
        problemSolve,
        answerCyuMcq,
        content_filter,
        chat_enrichment,
        contentIdentifierUserSelection
      } = chatgptResponseUtility;
      if (chatgptResponseUtility) {
        const summaryData = summary && summary.length > 0 ? summary : [];
        const quizMcqData = quizMcq && quizMcq.length > 0 ? quizMcq : [];
        const discussData = discuss && discuss.length > 0 && discuss ? discuss : [];
        const quizIintentData = quizIntent && quizIntent.length > 0 ? quizIntent : [];
        const summaryTopicData = summaryTopic && summaryTopic.length > 0 ? summaryTopic : [];
        const summaryEnrichmentData = summary_enrichment && summary_enrichment.length > 0 ? summary_enrichment : [];
        const mcqData = mcq && mcq.length > 0 ? mcq : [];
        const quizData = quiz && quiz.length > 0 ? quiz : [];
        const mcqGenerationData =
          mcqQuestionGeneration && mcqQuestionGeneration.length > 0 ? mcqQuestionGeneration : [];
        const commonAuditDataVal = commonAuditData && commonAuditData.length > 0 ? commonAuditData : [];
        const explainStandaloneData = explainStandalone && explainStandalone.length > 0 ? explainStandalone : [];
        const flashcardsData = flashcards && flashcards.length > 0 ? flashcards : [];
        const quizFreeResponseData = quizFreeResponse && quizFreeResponse.length > 0 ? quizFreeResponse : [];
        const quizValidateData = quizValidateIntent && quizValidateIntent.length > 0 ? quizValidateIntent : [];
        const salutationData = salutation && salutation.length > 0 ? salutation : [];
        const chatIntentData = chat_intent && chat_intent.length > 0 ? chat_intent : [];
        const problemSolveData = problemSolve && problemSolve.length > 0 ? problemSolve : [];
        const answerCyuMcqData = answerCyuMcq && answerCyuMcq.length > 0 ? answerCyuMcq : [];
        const contentFilterData = content_filter && content_filter.length > 0 ? content_filter : [];
        const chatEnrichmentData = chat_enrichment && chat_enrichment.length > 0 ? chat_enrichment : [];
        const contentIdentifierUserSelectionData =
          contentIdentifierUserSelection && contentIdentifierUserSelection.length > 0
            ? contentIdentifierUserSelection
            : [];
        const combineData = [
          ...summaryData,
          ...discussData,
          ...quizMcqData,
          ...quizIintentData,
          ...mcqData,
          ...mcqGenerationData,
          ...summaryTopicData,
          ...summaryEnrichmentData,
          ...commonAuditDataVal,
          ...quizData,
          ...chatIntentData,
          ...explainStandaloneData,
          ...flashcardsData,
          ...quizFreeResponseData,
          ...quizValidateData,
          ...salutationData,
          ...problemSolveData,
          ...answerCyuMcqData,
          ...contentFilterData,
          ...chatEnrichmentData,
          ...contentIdentifierUserSelectionData
        ];
        /* eslint-disable no-param-reassign */
        if (combineData && combineData[0] && combineData[0].type === 'chat_intent') {
          try {
            combineData[0].botResponse = JSON.parse(combineData[0].botResponse);
          } catch (err) {
            const { botResponse } = combineData[0];
            combineData[0] = { ...combineData[0], botResponse: botResponse[0] };
          }
        }
        const combineDatatoJS = JSON.parse(JSON.stringify(toJS(combineData)));
        if (
          combineData &&
          combineData[0] &&
          (combineData[0].type === 'quiz_topic_practice1' ||
            combineData[0].type === 'quiz_topic_practice2' ||
            combineData[0].type === 'quiz')
        ) {
          setIsTopicQuiz(true);
          return setDataValue(utils.sortByServerDateTime(JSON.parse(JSON.stringify(toJS(combineData)))));
        }
        if (combineDatatoJS[0] && Object.keys(combineDatatoJS[0]).length > 0) {
          rowProcessed = Object.entries(combineDatatoJS[0]).reduce((obj, [key, value]) => {
            if (key !== null && value !== null) {
              obj[key] = value;
            }
            return obj;
          }, {});
          if (rowProcessed.serverDateTime && isFeedback) {
            delete rowProcessed.serverDateTime;
          }
          if (rowDialogue && Object.keys(rowDialogue).length > 0) {
            rowDialogProcessed = Object.entries(rowDialogue).reduce((obj, [key, value]) => {
              if (key !== null && value !== null) {
                obj[key] = value;
              }
              return obj;
            }, {});
          }

          if (rowProcessed.type === 'chat_intent') {
            const { botResponse } = rowProcessed;
            let processBotResponse = '';
            try {
              processBotResponse =
                !_.isEmpty(botResponse) && typeof botResponse === 'string' && botResponse.trim()
                  ? [JSON.parse(botResponse)]
                  : botResponse;
            } catch (err) {
              const splitTwoText = botResponse.split('[BAD-GPT]');
              const normGPT = splitTwoText.length > 0 ? JSON.parse(splitTwoText[0].split('[NORM-GPT]')[1]) : '';
              const badGPT = normGPT.length > 0 ? JSON.parse(splitTwoText[1]) : '';
              processBotResponse = [normGPT, badGPT];
            }
            rowProcessed.botResponse = processBotResponse;
          }
          if (rowProcessed.type === constants.CONTEXT_IDENTIFIER_USER_SELECTION) {
            const { botResponse } = rowProcessed;
            rowProcessed.botResponse = [botResponse];
          }

          const rowChangedValue = {
            ...rowDialogProcessed,
            ...rowProcessed
          };

          /* eslint-enable camelcase */
          setDataValue(rowChangedValue);
        } else if (isFeedback && rowDialogue) {
          setDataValue(rowDialogue);
        }
      }
    });
  }, [
    gridData,
    isNextPageKey,
    chatgptResponseUtility,
    rowDialogue,
    userEmailDetails,
    userTocDetails,
    ChatgptUtilityIESUserData,
    isUserIdExcluded,
    isNextPageKeyEnable
  ]);

  useEffect(() => {
    if (dataValue?.bookId && dataValue?.requestId && !isFeedback) {
      const typeValue = dataValue?.type;
      const url = `${window.location.origin}${window.location.pathname}?bookId=${dataValue.bookId}&requestId=${dataValue.requestId}&type=${typeValue}`;
      // window.location.href = url;
      window.history.replaceState({}, '', url);
    }
  }, [dataValue]);

  const sorting = (value) => {
    if (order === 'ASC') {
      handleAscending('asc', value);
      SetOrder('DSC');
      setDirectionValue(value);
    }
    if (order === 'DSC') {
      handleAscending('des', value);
      SetOrder('ASC');
      setDirectionValue(value);
    }
  };

  const handleClose = () => {
    if (!isFeedback) {
      const url = `${window.location.origin}${window.location.pathname}`;
      window.history.replaceState({}, '', url);
    }
    openChatDialog(false);
    setModalIndex(1);
    setDataValue({});
  };

  const onColumnClick = (val) => {
    sorting(val);
  };
  const onRowClick = (val, index) => {
    handleEvent(val, index);
  };
  const hanldeGETUserId = (userId) => {
    const { userData } = ChatgptUtilityIESUserData;
    if (userId) {
      const dataMatch = userData && userData.find((data) => data.userId === userId);
      if (dataMatch) {
        setUserEmailDetails(dataMatch);
      } else {
        ChatgptUtilityIESUserData.fetch({ userId });
      }
    }
  };
  const onMouseEnter = (val) => {
    hanldeGETUserId(val);
  };

  const handleModalChange = (value) => {
    let modalArrData = gridData;
    if (isUserIdExcluded && internalUserIds?.userId.length > 0) {
      modalArrData = filteredArray;
    }
    if (value === 'prev' && modalIndex > 0) {
      const { requestId, bookId, type } = modalArrData[modalIndex - 1];
      setRowDialogue(modalArrData[modalIndex - 1]);
      setModalIndex(modalIndex - 1);
      if (isFeedback) {
        chatgptResponseUtility.fetch({ requestId, bookId, tenantId });
      } else {
        chatgptResponseUtility.fetch({ requestId, bookId, type, tenantId });
      }
    } else {
      const { requestId, bookId, type } = modalArrData[modalIndex + 1];
      setRowDialogue(modalArrData[modalIndex + 1]);
      setModalIndex(modalIndex + 1);
      if (isFeedback) {
        chatgptResponseUtility.fetch({ requestId, bookId, tenantId });
      } else {
        chatgptResponseUtility.fetch({ requestId, bookId, type, tenantId });
      }
    }
  };

  /* eslint-disable no-param-reassign */
  const handlePageChange = (e) => {
    e.preventDefault();
    let lastKey = {};
    if (isNextPageKey && isNextPageKey.ExclusiveStartKey) {
      const startKey = isNextPageKey.ExclusiveStartKey;
      lastKey = Object.entries(startKey).reduce((obj, [key, value]) => {
        if (key !== null && value !== null) {
          obj[key] = value;
        }
        return obj;
      }, {});
    }
    if (isNextPageKeyEnable?.ExclusiveStartKey?.bookId) {
      handleSearch({ lastKey: { ExclusiveStartKey: isNextPageKeyEnable } });
    } else {
      handleSearch({ lastKey: { ExclusiveStartKey: lastKey } });
    }
  };

  const getColumnConfig = () => {
    const params = {
      bookId: tenantName !== 'rplus' ? 'Course Id' : 'Book Id',
      hideUserColumn: configData?.hideUserColumn
    };
    if (selectedTab === ChatgptConfig.TabList.AUDIT_LOG) {
      return ColumnConfig.CommonDataGridColumn(params);
    }
    if (selectedTab === ChatgptConfig.TabList.FEEDBACK) {
      return ColumnConfig.CommonFeedbackColumn(params);
    }
    if (selectedTab === ChatgptConfig.TabList.TRENDS) {
      if (toggleBtnValue === 'count') {
        return ColumnConfig.CommonTrendsDataGridColumn(params);
      }
    }
    if (selectedTab === ChatgptConfig.TabList.CONTENT_MGMT) {
      return ColumnConfig.CommonContentManagementColumn;
    }
    return [];
  };

  const changePage = (newPage) => {
    setServerPage(newPage);
    handleSearch({ paginationData: { page: newPage, limit: rowsPerPage } });
  };

  return (
    <>
      {[ChatgptConfig.TabList.AUDIT_LOG, ChatgptConfig.TabList.FEEDBACK, ChatgptConfig.TabList.CONTENT_MGMT].includes(
        selectedTab
      ) ||
      (selectedTab === ChatgptConfig.TabList.TRENDS && toggleBtnValue === 'count') ? (
        <>
          {filteredArray.length > 0 && (
            <>
              <ExportToCSV
                headers={[
                  ...(getColumnConfig()?.map(({ key, name }) => ({ key, name })) || []),
                  { key: 'requestId', name: constants.REQUEST_ID }
                ]}
                data={filteredArray}
                fileTitle={isFeedback ? `${tenantName}FeedbackLogs` : `${tenantName}AuditLogs`}
              />
              <TableComponent
                columns={getColumnConfig()}
                data={filteredArray}
                onColumnClick={onColumnClick}
                order={order}
                onRowClick={toggleBtnValue === 'count' ? () => {} : onRowClick}
                selectedValue={selectedValue}
                directionValue={directionValue}
                userEmailDetails={userEmailDetails}
                onMouseEnter={onMouseEnter}
                isFromCommonDataGrid
                showSelectColumn={toggleBtnValue === 'count'}
                configData={configData}
              />
            </>
          )}
          {selectedTab === ChatgptConfig.TabList.CONTENT_MGMT && !showIngestion && (
            <CustomPagination
              serverPage={serverPage}
              totalPages={totalPages}
              paginationData={paginationData}
              rowsPerPage={rowsPerPage}
              changePage={changePage}
            />
          )}
          <br />
          {filteredArray.length > 0 && (
            <Box spacing={2}>
              <Stack sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'row' }}>
                {((isNextPageKey &&
                  isNextPageKey.ExclusiveStartKey &&
                  isNextPageKey.ExclusiveStartKey.bookId &&
                  isNextPageKey.ExclusiveStartKey.bookId.S !== null) ||
                  (isNextPageKeyEnable &&
                    isNextPageKeyEnable.ExclusiveStartKey &&
                    isNextPageKeyEnable.ExclusiveStartKey.bookId &&
                    isNextPageKeyEnable.ExclusiveStartKey.bookId !== null)) && (
                  <Box>
                    <ThemeProvider theme={themes}>
                      <Button
                        size="small"
                        color="primary"
                        className={classes.buttonStyle}
                        variant="contained"
                        onClick={(e) => handlePageChange(e)}
                      >
                        <Tooltip title="Load next set of data">Load More</Tooltip>
                      </Button>
                    </ThemeProvider>
                  </Box>
                )}
              </Stack>
            </Box>
          )}
        </>
      ) : (
        <DynamicListing
          listingData={gridData}
          startDate={startDate}
          endDate={endDate}
          tenantName={tenantName}
          tenantTitle={tenantTitle}
          tenantId={tenantId}
          searchFields={searchFields}
        />
      )}

      {openChat &&
        (!isTopicQuiz ? (
          <DynamicDialog
            openChat={openChat}
            dataValue={dataValue}
            userTocDetails={userTocDetails}
            dataLength={dataLength}
            modalIndex={modalIndex}
            filterPromt={constants.FILTER_PROMPT}
            isFeedback={isFeedback}
            loaderStatusResponse={loaderStatusResponse}
            chatgptUtilityResponseStatus={chatgptUtilityResponseStatus}
            handleClose={handleClose}
            handleModalChange={handleModalChange}
            tenantTitle={tenantTitle}
            tenantId={tenantId}
          />
        ) : (
          <TopicQuizDialogComponent
            openChat={openChat}
            dataValue={dataValue}
            userTocDetails={userTocDetails}
            dataLength={dataLength}
            modalIndex={modalIndex}
            filterPromt={constants.FILTER_PROMPT}
            loaderStatusResponse={loaderStatusResponse}
            chatgptUtilityResponseStatus={chatgptUtilityResponseStatus}
            handleClose={handleClose}
            handleModalChange={handleModalChange}
          />
        ))}
    </>
  );
});

DynamicDataGrid.propTypes = {
  handleSearch: PropTypes.func.isRequired,
  gridData: PropTypes.array.isRequired,
  classes: PropTypes.object.isRequired,
  handleAscending: PropTypes.func.isRequired,
  isNextPageKey: PropTypes.object,
  selectedTab: PropTypes.string,
  toggleBtnValue: PropTypes.string.isRequired,
  chatgptResponseUtility: shapes.modelOf(ResponseModel).isRequired,
  chatgptUtilityResponseStatus: shapes.state.isRequired,
  ChatgptUtilityIESUserData: shapes.modelOf(ChatgptUtilityIESUser).isRequired,
  chatgptMappingUtility: shapes.modelOf(ChatgptUtilityMapping).isRequired,
  tenantName: PropTypes.string,
  tenantTitle: PropTypes.string,
  tenantId: PropTypes.string,
  viewType: PropTypes.string,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  tenantConfig: PropTypes.object.isRequired,
  isUserIdExcluded: PropTypes.bool.isRequired,
  internalUsers: shapes.modelOf(InternalUsersList).isRequired,
  isNextPageKeyEnable: PropTypes.object,
  paginationData: PropTypes.object.isRequired,
  showIngestion: PropTypes.bool.isRequired,
  searchFields: PropTypes.object
};

DynamicDataGrid.defaultProps = {
  isNextPageKey: {},
  selectedTab: '',
  tenantName: '',
  tenantTitle: '',
  tenantId: '',
  viewType: '',
  isNextPageKeyEnable: {},
  searchFields: {}
};

export default withStyles(styles)(
  observer(
    inject(
      'chatgptResponseUtility',
      'chatgptUtilityResponseStatus',
      'ChatgptUtilityIESUserData',
      'chatgptMappingUtility',
      'internalUsers'
    )(DynamicDataGrid)
  )
);
